import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react'
import { InteractionTag, Text } from '@fluentui/react-components'

import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { InvoiceApprovalStatus, UserInvoiceApprovalDto } from './InvoiceApprovalOverview'
import { useFormikContext } from 'formik'
import PersonAvatar from '../../components/PersonAvatar'
import useAxios from '../../hooks/useAxios'
import useUsers from '../../hooks/useUsers'
import BaseTimeOverview, { BaseTimeOverviewProps, ExtendedTimeOverviewDto, TimeFilterType } from '../../v2/pages/Time/BaseTimeOverview'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { Billable } from '../../config/registration/timeConfig'
import { getTemplateInterval } from '../../components/Form/Finance/Invoice/InvoiceFile'
import { InvoiceApprovalMutationDto, UserInvoiceApprovalMutationDto, ViewInvoiceApprovalComponentProps } from './ViewInvoiceApproval'

export type ViewInvoiceApprovalTimesProps = ViewInvoiceApprovalComponentProps & {
    setTimesCount: Dispatch<SetStateAction<number>>
    setTimes: Dispatch<SetStateAction<ExtendedTimeOverviewDto[]>>
    selectedTimes: ExtendedTimeOverviewDto[]
}

export function TimesHeaderFilterTags(props: BaseTimeOverviewProps & { userApprovals: UserInvoiceApprovalMutationDto[] }) {
    const { values, setValues } = useFormikContext<TimeFilterType>()
    const overviewStyles = useOverviewStyles()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNewWide), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {props.userApprovals?.map((item, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === item.userId}
                    onClick={() => setValues({ ...props.defaultFilterValues, users: [item.userId], tag: item.userId })}
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <PersonAvatar name={getUserById(item.userId)?.fullName} color={'colorful'} size={24} />
                </InteractionTag>
            ))}
        </div>
    )
}

export default function ViewInvoiceApprovalTimes(props: ViewInvoiceApprovalTimesProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    const defaultFilterValues = useMemo<TimeFilterType>(
        () => ({
            from: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).start.toISODate(),
            to: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).end.toISODate(),
            users: [],
            isBilled: props.approvalStatus === InvoiceApprovalStatus.Done,
            files: values.fileIds ? values.fileIds : [],
            fileOwners: [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
            billable: [Billable.Billable, Billable.Offered],
            attributes: [],
            query: '',
        }),
        [values, props.invoiceTemplate],
    )

    const fixedFilterValues = useMemo<TimeFilterType>(
        () => ({
            files: values.fileIds || [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
        }),
        [values, props.invoiceTemplate],
    )

    return (
        <div className={props.hiddenClassName ? props.hiddenClassName : undefined}>
            {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
            {values.fileIds?.length > 0 && (
                <BaseTimeOverview
                    baseUrl={`/invoice-approval/${props.approval?.id}/time`}
                    hideFileField
                    hideInvoiceField
                    focusQueryOnRender={false}
                    renderLink={!!values.id}
                    headerDefaultFilterTags={filterProps => (
                        <TimesHeaderFilterTags
                            {...(filterProps as BaseTimeOverviewProps)}
                            baseUrl={`/invoice-approval/${props.approval?.id}/time`}
                            userApprovals={values.userApprovals}
                        />
                    )}
                    fixedFilterValues={fixedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    batchSizeItems={50}
                    onCountItems={(_column, result) => props.setTimesCount(result.total)}
                    onLoadItems={(_column, _columnPages, items) => props.setTimes([...items])}
                    alwaysLoadAllItems
                    isItemSelectable={() => props.isApprovalActive}
                    isAllItemsSelectable={() => props.isApprovalActive}
                    isItemSelected={item => props.selectedTimes.includes(item)}
                    isAllItemsSelected={items => items.filter(item => !props.selectedTimes.includes(item)).length === 0}
                    onItemSelected={(data, item) => {
                        if (data.checked === true) {
                            setFieldValue('times', [...values.times, item.id])
                            if (values.id) {
                                axios.post<UserInvoiceApprovalDto[]>(`/finance/invoice-approval/${values.id}/time?id=${item.id}`).then(response => {
                                    if (!!response?.data) {
                                        setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                    }
                                })
                            }
                        } else {
                            setFieldValue('times', [...values.times.filter(time => time !== item.id)])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/time?id=${item.id}`)
                            }
                        }
                    }}
                    onAllItemsSelected={(data, items) => {
                        if (data.checked === true) {
                            setFieldValue(
                                'times',
                                items.map(item => item.id),
                            )
                            if (values.id) {
                                axios
                                    .post<
                                        UserInvoiceApprovalDto[]
                                    >(`/finance/invoice-approval/${values.id}/time?id=${items.map(item => item.id).join(',')}`)
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('times', [])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/time?id=${items.map(item => item.id).join(',')}`)
                            }
                        }
                    }}
                    selectedItems={props.selectedTimes}
                />
            )}
        </div>
    )
}
